import React, { FC, useRef, useState } from "react";
import { Formik, Form, FormikProps } from "formik";
import CustomSearchField from "../Fields/CustomSearchField/CustomSearchField";
import { useTranslations } from "next-intl";
import styles from "./CarPassengerForm.module.scss";
import CustomSelectSearch from "../selects/SearchFormSelector/SearchFormSelector";

interface CarPassengerFormProps {
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
}

interface FormValues {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  countryOfResidence: string;
  language: string;
  travelIntend: string;
  comments: string;
}

const CarPassengerForm: FC<CarPassengerFormProps> = ({ setCurrentStep }) => {
  const t = useTranslations();

  const [dropdowns, setDropdowns] = useState({
    countryOfResidence: false,
    language: false,
    travelIntend: false,
  });

  const dropdownRefs = {
    countryOfResidence: useRef<HTMLDivElement>(null),
    language: useRef<HTMLDivElement>(null),
    travelIntend: useRef<HTMLDivElement>(null),
  };

  const handleDropdownToggle = (key: keyof typeof dropdowns) => {
    setDropdowns((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const initialValues: FormValues = {
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    countryOfResidence: "",
    language: "",
    travelIntend: "",
    comments: "",
  };

  const countryOptions = [
    { label: "Egypt", value: "EG" },
    { label: "UAE", value: "AE" },
    { label: "Saudi Arabia", value: "SA" },
  ];

  const languageOptions = [
    { label: "English", value: "en" },
    { label: "Arabic", value: "ar" },
  ];

  const travelOptions = [
    { label: "Business", value: "business" },
    { label: "Leisure", value: "leisure" },
    { label: "Other", value: "other" },
  ];

  const handleSubmit = (values: FormValues) => {
    setCurrentStep(3);
  };

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit}>
      {({
        values,
        setFieldValue,
        getFieldProps,
        errors,
      }: FormikProps<FormValues>) => (
        <Form className="flex flex-col c-md:w-[887px] w-full gap-6 rounded-lg">
          <h1 className="font-semibold c-md:text-[26px] text-lg text-primaryText leading-10 text-start">
            {t("formGuest.passengerDetails")}
          </h1>

          <div className="c-md:grid c-md:grid-cols-2 flex flex-col gap-4 w-full">
            <CustomSearchField
              name="firstName"
              label={t("formGuest.firstName")}
              className={`w-full !border-[#e0e0e0] ${
                errors.firstName && "!border-red-500"
              }`}
              content={
                <input
                  type="text"
                  className={styles.placeholder}
                  {...getFieldProps("firstName")}
                  autoComplete="off"
                />
              }
            />

            <CustomSearchField
              name="lastName"
              label={t("formGuest.lastName")}
              className={`w-full !border-[#e0e0e0] ${
                errors.lastName && "!border-red-500"
              }`}
              content={
                <input
                  type="text"
                  className={styles.placeholder}
                  {...getFieldProps("lastName")}
                  autoComplete="off"
                />
              }
            />

            <CustomSearchField
              name="email"
              label={t("formGuest.email")}
              className={`w-full !border-[#e0e0e0] ${
                errors.email && "!border-red-500"
              }`}
              content={
                <input
                  type="email"
                  className={styles.placeholder}
                  {...getFieldProps("email")}
                  autoComplete="off"
                />
              }
            />

            <CustomSearchField
              name="phoneNumber"
              label={t("formGuest.phoneNumber")}
              className={`w-full !border-[#e0e0e0] ${
                errors.phoneNumber && "!border-red-500"
              }`}
              content={
                <input
                  type="tel"
                  className={styles.placeholder}
                  {...getFieldProps("phoneNumber")}
                  autoComplete="off"
                />
              }
            />
          </div>

          <div className="c-md:grid c-md:grid-cols-3 flex flex-col gap-4 w-full">
            <CustomSearchField
              name="countryOfResidence"
              label={t("formGuest.country")}
              className={`w-full !border-[#e0e0e0] relative !text-primaryText ${
                errors.countryOfResidence && "!border-red-500"
              }`}
              onClick={() => handleDropdownToggle("countryOfResidence")}
              ref={dropdownRefs.countryOfResidence}
              content={
                <CustomSelectSearch
                  options={countryOptions}
                  value={values.countryOfResidence}
                  onChange={(value) =>
                    setFieldValue("countryOfResidence", value)
                  }
                  showOption={true}
                  dropdownIsOpen={dropdowns.countryOfResidence}
                  hasSearch
                  className="w-full "
                  hasArrow
                  isCountry

                />
              }
            />

            <CustomSearchField
              name="language"
              label={t("formGuest.language")}
              className={`w-full !border-[#e0e0e0] relative ${
                errors.language && "!border-red-500"
              }`}
              onClick={() => handleDropdownToggle("language")}
              ref={dropdownRefs.language}
              content={
                <CustomSelectSearch
                  options={languageOptions}
                  value={values.language}
                  onChange={(value) => setFieldValue("language", value)}
                  showOption={true}
                  dropdownIsOpen={dropdowns.language}
                  hasSearch
                  className="w-full"
                  hasArrow
                />
              }
            />

            <CustomSearchField
              name="travelIntend"
              label={t("formGuest.travelIntend")}
              className={`w-full !border-[#e0e0e0] relative ${
                errors.travelIntend && "!border-red-500"
              }`}
              onClick={() => handleDropdownToggle("travelIntend")}
              ref={dropdownRefs.travelIntend}
              content={
                <CustomSelectSearch
                  options={travelOptions}
                  value={values.travelIntend}
                  onChange={(value) => setFieldValue("travelIntend", value)}
                  showOption={true}
                  dropdownIsOpen={dropdowns.travelIntend}
                  hasSearch
                  className="w-full"
                  hasArrow
                />
              }
            />
          </div>

          <CustomSearchField
            name="comments"
            label={t("formGuest.commentsForDriver")}
            className="w-full !border-[#e0e0e0] relative !max-h-36"
            content={
              <textarea
                rows={2}
                className={`${styles.placeholder} resize-none`}
                {...getFieldProps("comments")}
              />
            }
          />

          <button
            type="submit"
            className="flex c-md:w-[480px] w-full h-[52px] items-center justify-center bg-primary rounded-3xl text-white text-lg font-medium hover:opacity-90 transition-opacity"
          >
            {t("formGuest.placeBooking")}
          </button>
        </Form>
      )}
    </Formik>
  );
};

export default CarPassengerForm;
